/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */
import './public-path';
import './saga-hooks';
import { createBrowserHistory } from 'history';
import { ENV } from './env';
import { Provider } from 'react-redux';
import { createRoot } from 'react-dom/client';
import { Route, Switch } from 'react-router-dom';
import { SentryConfigure } from '@pisano/feedback-core/app/utils/sentry';
import { SetFeedbackCoreConfig } from '@pisano/feedback-core/app/config';
import App from '@pisano/feedback-core/app/components/App';
import appCss from '@pisano/feedback-core/app/app.scss';
import Chat from './containers/Chat';
import configureStore from '@pisano/feedback-core/app/configureStore';
import ErrorBoundary from '@pisano/feedback-core/app/components/ErrorBoundary';
import feedbackWebCss from './styles/feedback-web.scss';
import Flow from '@pisano/feedback-core/app/containers/Flow/Loadable';
import ProtectedRoute from './components/ProtectedRoute';
import React from 'react';
import RouterProvider from '@pisano/feedback-core/app/components/RouterProvider';
import translations from './translations.json';
import Widget from './containers/Widget';
import { startFeedbackQueueRuntime } from './utils/feedbackQueueRuntime';
import { triggerPostMessage } from '@pisano/feedback-core/app/utils/postMessage';

window.__PSN_BUILD_INFO__ = __PSN_BUILD_INFO__;

SetFeedbackCoreConfig(ENV);
SentryConfigure();

import('@pisano/feedback-core/app/containers/Translate').then(({ configureTranslate }) => {
  configureTranslate({
    translations,
  });
});

// Create redux store with history
const initialState = {};
const history = createBrowserHistory();
const store = configureStore(initialState, history);
const MOUNT_NODE = document.getElementById('app');
let appRoot;
let stopFeedbackQueue;

const initGtm = (id) => {
  if (!ENV.GTM_ENABLED) {
    return;
  }
  const script = document.createElement('script');
  script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),
    dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='
    +i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`;

  const noScript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
  iframe.height = 0;
  iframe.width = 0;
  iframe.style.cssText = 'display:none;visibility:hidden';
  noScript.appendChild(iframe);

  document.head.appendChild(script);
  document.body.appendChild(noScript);
};

const init = () => {
  if (!stopFeedbackQueue) {
    // Queue messages use the same transport as Flow messages.
    stopFeedbackQueue = startFeedbackQueueRuntime({
      apiUrl: ENV.API_URL,
      history,
      enabled: true,
      emit: ({ type, data }) => triggerPostMessage({ type, data }),
    });
  }

  const errorWrapper = (p) => <div className="center-absolute">{p.children}</div>;
  const notFoundRender = () => {
    const path = '__NOT_FOUND_PATH__';

    window.location.href = path && !path.includes('NOT_FOUND') ? path : 'https://www.pisano.co/';
    return null;
  };

  // Keep a single React 18 root for normal renders and HMR. Recreating a root
  // for the same container is unsupported and would break existing entry-point behavior.
  if (!appRoot) {
    appRoot = createRoot(MOUNT_NODE);
  }

  // Strict Mode covers the full-page application's providers and router. The
  // embeddable widget owns a separate public root in Widget/init.js.
  appRoot.render(
    <React.StrictMode>
      <Provider store={store}>
        <RouterProvider history={history}>
          <ErrorBoundary loadCss={appCss} wrapper={errorWrapper}>
            <App>
              <Switch>
                <ProtectedRoute
                  component={Flow}
                  path="/web_flow_preview"
                  loadCss={feedbackWebCss}
                  exact
                  requiredQueryParams="flow_id"
                  componentProps={{
                    defaultPlaceholder: 'webPlaceholder',
                    timeoutEnabled: false,
                    animationsEnabled: false,
                    queryParams: {
                      isPreview: true,
                    },
                  }}
                />
                <ProtectedRoute
                  component={Flow}
                  path="/flow_preview"
                  loadCss={feedbackWebCss}
                  exact
                  requiredQueryParams="flow_id"
                  componentProps={{
                    defaultPlaceholder: 'webPlaceholder',
                    timeoutEnabled: false,
                    insideWidget: true,
                    animationsEnabled: false,
                    queryParams: {
                      isPreview: true,
                      previewType: 'export',
                    },
                  }}
                />
                <ProtectedRoute
                  component={Flow}
                  path="/feedback"
                  exact
                  requiredQueryParams="node_id"
                  componentProps={{
                    defaultPlaceholder: 'webPlaceholder',
                    timeoutEnabled: false,
                    animationsEnabled: false,
                  }}
                />
                <ProtectedRoute
                  component={Flow}
                  path="/web_feedback/:linkId/:responses?"
                  exact
                  loadCss={feedbackWebCss}
                  componentProps={{
                    defaultPlaceholder: 'webPlaceholder',
                    timeoutEnabled: false,
                    animationsEnabled: false,
                  }}
                />
                <ProtectedRoute
                  component={Flow}
                  path="/web_feedback"
                  exact
                  requiredQueryParams="node_id"
                  componentProps={{
                    defaultPlaceholder: 'webPlaceholder',
                    timeoutEnabled: false,
                    animationsEnabled: false,
                  }}
                />
                <ProtectedRoute component={Chat} path="/chat" exact requiredQueryParams={['feedback_id', 'token']} />
                <ProtectedRoute component={Widget} path="/widget" exact requiredQueryParams="code" />
                <ProtectedRoute
                  component={Flow}
                  path="/:linkId([a-z0-9]{10})/:responses?"
                  exact
                  sensitive
                  loadCss={feedbackWebCss}
                  componentProps={{
                    defaultPlaceholder: 'webPlaceholder',
                    timeoutEnabled: false,
                    animationsEnabled: false,
                  }}
                />
                <Route render={notFoundRender} />
              </Switch>
            </App>
          </ErrorBoundary>
        </RouterProvider>
      </Provider>
    </React.StrictMode>,
  );
};

const unmount = () => {
  stopFeedbackQueue?.();
  stopFeedbackQueue = undefined;

  if (!appRoot) return;

  appRoot.unmount();
  appRoot = undefined;
};

initGtm(ENV.GTM_ID);
init();

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['@pisano/feedback-core/app/components/App'], () => {
    unmount();
    init();
  });
}
